import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Phone, Mail, MapPin, Check, Loader2 } from 'lucide-react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import LogoWatermark from '../components/site/LogoWatermark';

const TOTAL_STEPS = 4;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const TextField = ({ label, value, onChange, type = 'text', required }) => (
  <label className="block">
    <span className="sr-only">{label}</span>
    <div className="relative rounded-2xl border-2 border-white/20 hover:border-white/40 focus-within:border-[#2EF5FF] transition-colors bg-[#12367F]">
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder=" "
        className="peer w-full bg-transparent px-6 pt-7 pb-3 font-display uppercase text-2xl md:text-3xl text-white placeholder-transparent focus:outline-none"
      />
      <span className="pointer-events-none absolute left-6 top-3 text-xs font-display uppercase tracking-wider text-white/60 peer-focus:text-[#2EF5FF] transition-colors">
        {label}{required && ' *'}
      </span>
    </div>
  </label>
);

const SocialField = ({ platform, value, onChange }) => (
  <label className="block">
    <div className="rounded-full border-2 border-white/20 hover:border-white/40 focus-within:border-[#2EF5FF] transition-colors bg-transparent">
      <div className="flex items-center px-6 py-4">
        <span className="font-display uppercase text-base md:text-lg tracking-wide text-white/85 w-32 shrink-0">
          {platform}
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`@your-${platform.toLowerCase()}`}
          className="flex-1 bg-transparent text-white placeholder-white/30 focus:outline-none text-base"
        />
      </div>
    </div>
  </label>
);

const Toggle = ({ value, onChange }) => {
  const isTalent = value === 'talent';
  return (
    <div className="flex items-center gap-4">
      <span className="font-display uppercase text-2xl md:text-3xl text-white">I&apos;m a</span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange('talent')}
          className={`relative px-7 py-3 rounded-full border-2 font-display uppercase tracking-wide text-lg md:text-xl transition-all ${
            isTalent
              ? 'bg-[#2EF5FF] text-[#12367F] border-[#2EF5FF]'
              : 'bg-transparent text-white/60 border-white/30 hover:border-white/60'
          }`}
        >
          <span className={`inline-block h-3 w-3 rounded-full mr-2 align-middle ${isTalent ? 'bg-[#12367F]' : 'bg-white/40'}`} />
          Talent
        </button>
        <button
          type="button"
          onClick={() => onChange('brand')}
          className={`relative px-7 py-3 rounded-full border-2 font-display uppercase tracking-wide text-lg md:text-xl transition-all ${
            !isTalent
              ? 'bg-[#2EF5FF] text-[#12367F] border-[#2EF5FF]'
              : 'bg-transparent text-white/60 border-white/30 hover:border-white/60'
          }`}
        >
          <span className={`inline-block h-3 w-3 rounded-full mr-2 align-middle ${!isTalent ? 'bg-[#12367F]' : 'bg-white/40'}`} />
          Brand
        </button>
      </div>
    </div>
  );
};

const Contact = () => {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [type, setType] = useState('talent');
  const [data, setData] = useState({
    firstName: '', lastName: '', phone: '', email: '',
    instagram: '', tiktok: '', facebook: '', youtube: '',
    company: '', role: '', niche: '', followers: '',
    budget: '', timeline: '', goals: '', platforms: [], message: ''
  });

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const togglePlatform = (p) => setData((d) => ({
    ...d,
    platforms: d.platforms.includes(p) ? d.platforms.filter((x) => x !== p) : [...d.platforms, p]
  }));

  const next = () => setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        type,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        email: data.email,
        instagram: data.instagram,
        tiktok: data.tiktok,
        facebook: data.facebook,
        youtube: data.youtube,
        company: data.company,
        role: data.role,
        niche: data.niche,
        followers: data.followers,
        budget: data.budget,
        timeline: data.timeline,
        platforms: data.platforms,
        message: data.message
      };
      await axios.post(`${API}/enquiries`, payload, { timeout: 15000 });
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Something went wrong. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Validation failed. Please check your inputs.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = (step / TOTAL_STEPS) * 100;

  return (
    <main className="min-h-screen" style={{ background: '#12367F' }}>
      <Navbar />

      <section className="pt-32 md:pt-40 pb-20 md:pb-28 relative overflow-hidden">
        {/* Decorative pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
          backgroundImage: 'radial-gradient(#2EF5FF 1.5px, transparent 1.5px)',
          backgroundSize: '28px 28px'
        }} />
        <LogoWatermark size={500} opacity={0.07} position="bottom-right" />

        <div className="container-x px-6 relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Left column */}
            <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
              <h1
                className="font-display uppercase text-balance"
                style={{ fontSize: 'clamp(3rem, 8vw, 7.5rem)', lineHeight: 0.88 }}
              >
                <span className="text-white">Hey you,</span>
                <br />
                <span style={{ color: '#2EF5FF' }}>Don&apos;t be shy</span>
              </h1>
              <p className="mt-8 text-white/75 text-base md:text-lg leading-relaxed max-w-md">
                Creators, we want to hear from you. Brands, we have the creators for your next
                campaign. Fill out our enquiry form or use the contact details below to get
                connected.
              </p>

              <div className="mt-10 space-y-4">
                <a
                  href="tel:+447400376317"
                  className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl border-2 border-[#2EF5FF] text-[#2EF5FF] font-display uppercase tracking-wide text-lg md:text-xl hover:bg-[#2EF5FF] hover:text-[#12367F] transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  +44 7400 376317
                </a>
                <a
                  href="mailto:info@nasugroup.com"
                  className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl border-2 border-[#2EF5FF] text-[#2EF5FF] font-display uppercase tracking-wide text-lg md:text-xl hover:bg-[#2EF5FF] hover:text-[#12367F] transition-colors"
                >
                  <Mail className="h-5 w-5" />
                  info@nasugroup.com
                </a>
                <div className="flex items-start gap-3 px-6 py-4 rounded-2xl border-2 border-[#2EF5FF] text-[#2EF5FF]">
                  <MapPin className="h-5 w-5 mt-0.5 shrink-0" />
                  <span className="font-display uppercase tracking-wide text-lg md:text-xl leading-snug">
                    128 City Road, London, United Kingdom, EC1V 2NX
                  </span>
                </div>
              </div>
            </div>

            {/* Right column - form */}
            <div className="lg:col-span-7">
              {submitted ? (
                <div className="rounded-3xl border-2 border-[#2EF5FF] p-10 md:p-14 text-center bg-[#2e3e2e]">
                  <div className="mx-auto h-16 w-16 rounded-full bg-[#2EF5FF] grid place-items-center mb-6">
                    <Check className="h-8 w-8 text-[#12367F]" />
                  </div>
                  <h2 className="font-display uppercase text-white" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', lineHeight: 0.9 }}>
                    You&apos;re in. <span style={{ color: '#2EF5FF' }}>We&apos;ll be in touch.</span>
                  </h2>
                  <p className="mt-4 text-white/75 max-w-md mx-auto">
                    Thanks for the message {data.firstName || ''} — our team will get back to you within 1–2 business days.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setSubmitted(false); setError(''); setStep(1); setData({
                      firstName: '', lastName: '', phone: '', email: '',
                      instagram: '', tiktok: '', facebook: '', youtube: '',
                      company: '', role: '', niche: '', followers: '',
                      budget: '', timeline: '', goals: '', platforms: [], message: ''
                    }); }}
                    className="btn-base btn-accent mt-8"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-10">
                  <Toggle value={type} onChange={setType} />

                  {/* Progress */}
                  <div>
                    <div className="flex items-end justify-between mb-3">
                      <p className="text-white/70 text-sm">Step {step} of {TOTAL_STEPS}</p>
                      <p className="text-white/50 text-xs uppercase tracking-wider">{Math.round(progress)}%</p>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-white/15 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${progress}%`, background: '#2EF5FF' }}
                      />
                    </div>
                  </div>

                  {/* Step 1: Basics + socials */}
                  {step === 1 && (
                    <div className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <TextField label="First Name" value={data.firstName} onChange={(v) => update('firstName', v)} required />
                        <TextField label="Last Name" value={data.lastName} onChange={(v) => update('lastName', v)} required />
                        <TextField label="Phone Number" type="tel" value={data.phone} onChange={(v) => update('phone', v)} required />
                        <TextField label="Email" type="email" value={data.email} onChange={(v) => update('email', v)} required />
                      </div>

                      <div className="pt-4">
                        <p className="font-display uppercase text-white text-2xl md:text-3xl mb-4">
                          Add your social links
                        </p>
                        <div className="space-y-3">
                          <SocialField platform="Instagram" value={data.instagram} onChange={(v) => update('instagram', v)} />
                          <SocialField platform="TikTok" value={data.tiktok} onChange={(v) => update('tiktok', v)} />
                          <SocialField platform="Facebook" value={data.facebook} onChange={(v) => update('facebook', v)} />
                          <SocialField platform="YouTube" value={data.youtube} onChange={(v) => update('youtube', v)} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2: About you / brand */}
                  {step === 2 && (
                    <div className="space-y-6">
                      <p className="font-display uppercase text-white text-2xl md:text-3xl">
                        {type === 'talent' ? 'A bit about you' : 'A bit about your brand'}
                      </p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <TextField
                          label={type === 'talent' ? 'Stage / Creator Name' : 'Company Name'}
                          value={data.company}
                          onChange={(v) => update('company', v)}
                        />
                        <TextField
                          label={type === 'talent' ? 'Primary Niche' : 'Your Role'}
                          value={data.role}
                          onChange={(v) => update('role', v)}
                        />
                        <TextField
                          label={type === 'talent' ? 'Total Followers' : 'Industry'}
                          value={data.niche}
                          onChange={(v) => update('niche', v)}
                        />
                        <TextField
                          label={type === 'talent' ? 'Top Platform' : 'Website'}
                          value={data.followers}
                          onChange={(v) => update('followers', v)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 3: Project / preferences */}
                  {step === 3 && (
                    <div className="space-y-6">
                      <p className="font-display uppercase text-white text-2xl md:text-3xl">
                        {type === 'talent' ? 'Your goals' : 'Your project'}
                      </p>

                      <div>
                        <p className="text-white/70 text-sm mb-3 uppercase tracking-wider">Platforms of interest</p>
                        <div className="flex flex-wrap gap-2">
                          {['Instagram', 'TikTok', 'YouTube', 'Twitch', 'X / Twitter', 'LinkedIn'].map((p) => {
                            const on = data.platforms.includes(p);
                            return (
                              <button
                                type="button"
                                key={p}
                                onClick={() => togglePlatform(p)}
                                className={`px-5 py-2.5 rounded-full border-2 text-sm md:text-base font-medium transition-all ${
                                  on
                                    ? 'bg-[#2EF5FF] text-[#12367F] border-[#2EF5FF]'
                                    : 'bg-transparent text-white/80 border-white/25 hover:border-white/60'
                                }`}
                              >
                                {p}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <TextField
                          label={type === 'brand' ? 'Estimated Budget' : 'Annual Earnings Goal'}
                          value={data.budget}
                          onChange={(v) => update('budget', v)}
                        />
                        <TextField
                          label="Timeline"
                          value={data.timeline}
                          onChange={(v) => update('timeline', v)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 4: Message */}
                  {step === 4 && (
                    <div className="space-y-6">
                      <p className="font-display uppercase text-white text-2xl md:text-3xl">
                        Anything else we should know?
                      </p>
                      <label className="block">
                        <div className="rounded-2xl border-2 border-white/20 focus-within:border-[#2EF5FF] transition-colors bg-[#12367F]">
                          <textarea
                            value={data.message}
                            onChange={(e) => update('message', e.target.value)}
                            rows={6}
                            placeholder="Tell us about your goals, audience, brand vision..."
                            className="w-full bg-transparent px-6 py-5 text-white placeholder-white/30 focus:outline-none text-base resize-none"
                          />
                        </div>
                      </label>

                      {/* Summary */}
                      <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
                        <p className="font-display uppercase text-white text-xl mb-3">Quick recap</p>
                        <dl className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <dt className="text-white/50 uppercase tracking-wider text-xs">Type</dt>
                            <dd className="text-white capitalize">{type}</dd>
                          </div>
                          <div>
                            <dt className="text-white/50 uppercase tracking-wider text-xs">Name</dt>
                            <dd className="text-white">{data.firstName} {data.lastName}</dd>
                          </div>
                          <div>
                            <dt className="text-white/50 uppercase tracking-wider text-xs">Email</dt>
                            <dd className="text-white truncate">{data.email || '—'}</dd>
                          </div>
                          <div>
                            <dt className="text-white/50 uppercase tracking-wider text-xs">Phone</dt>
                            <dd className="text-white">{data.phone || '—'}</dd>
                          </div>
                          {data.platforms.length > 0 && (
                            <div className="col-span-2">
                              <dt className="text-white/50 uppercase tracking-wider text-xs">Platforms</dt>
                              <dd className="text-white">{data.platforms.join(', ')}</dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    </div>
                  )}

                  {/* Error banner */}
                  {error && (
                    <div className="rounded-2xl border-2 border-red-400/60 bg-red-500/10 px-5 py-4 text-sm text-red-200">
                      {error}
                    </div>
                  )}

                  {/* Nav buttons */}
                  <div className="flex items-center justify-between pt-4">
                    <button
                      type="button"
                      onClick={back}
                      disabled={step === 1 || submitting}
                      className={`btn-base btn-outline-light ${step === 1 || submitting ? 'opacity-30 pointer-events-none' : ''}`}
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </button>

                    {step < TOTAL_STEPS ? (
                      <button type="button" onClick={next} className="btn-base btn-accent">
                        Next step
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    ) : (
                      <button type="submit" disabled={submitting} className={`btn-base btn-accent ${submitting ? 'opacity-70 pointer-events-none' : ''}`}>
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit enquiry
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Contact;
